import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/types";
import type { MyRobotDetail } from "@/entities/user-robot";
import { AmountPresets } from "@/features/amount-presets";
import { useTopupRobot } from "@/features/topup-robot";
import { classify } from "@/shared/api";
import { compareMoney, formatDay, formatMoney } from "@/shared/lib";
import { AmountInput, Button, Sheet, useToast } from "@/shared/ui";

type TopUpResult = components["schemas"]["TopUpResult"];

/**
 * «Добавить средства» в работающего робота (ТЗ §18).
 *
 * Лист, а не отдельный флоу страниц: пользователь не должен терять из виду
 * робота, которому доливает капитал.
 *
 * Ключевое: НОВЫЙ КАПИТАЛ ДО ПОДТВЕРЖДЕНИЯ НЕ ПОКАЗЫВАЕТСЯ. Сложить текущий
 * капитал с введённой суммой — это арифметика над деньгами на клиенте, чего
 * правило ТЗ №5 не разрешает. Цифра приходит в ответе (`invested_total`) и
 * показывается вторым состоянием того же листа, вместе с датой подключения
 * (§19) — то есть ровно тем, что действительно произошло на сервере.
 */
export function TopupSheet({
  robot,
  balance,
  onClose,
}: {
  robot: MyRobotDetail;
  balance: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const topup = useTopupRobot(robot.id);

  const [amount, setAmount] = useState(robot.robot.min_topup_amount);
  const [error, setError] = useState<string | undefined>(undefined);
  const [done, setDone] = useState<TopUpResult | null>(null);

  const minTopup = robot.robot.min_topup_amount;

  const submit = () => {
    setError(undefined);
    topup.mutate(amount, {
      onSuccess: (result) => {
        setDone(result);
      },
      onError: (mutationError) => {
        const reaction = classify(mutationError);

        if (reaction.kind === "inline" || reaction.kind === "insufficient") {
          // И «меньше минимума», и «не хватает денег» — это про поле суммы,
          // поэтому оба показываются у поля, а не тостом поверх листа.
          setError(
            reaction.kind === "inline"
              ? reaction.message
              : t("myRobots.topup.notEnough", {
                  amount: formatMoney(reaction.available ?? "0.00", { suffix: "USDT" }),
                }),
          );
          return;
        }

        toast.show({
          tone: "loss",
          title: reaction.kind === "conflict" ? reaction.message : t("states.error.title"),
        });
      },
    });
  };

  if (done !== null) {
    return (
      <Sheet
        title={t("myRobots.topup.doneTitle")}
        onClose={onClose}
        footer={
          <Button size="cta" block onClick={onClose}>
            {t("common.done")}
          </Button>
        }
      >
        <dl className="flex w-full flex-col">
          <div className="flex items-center justify-between gap-3 py-2">
            <dt className="text-caption font-normal text-text-secondary">
              {t("myRobots.topup.newCapital")}
            </dt>
            <dd className="text-caption font-semibold text-text-primary">
              {formatMoney(done.invested_total, { suffix: "USDT" })}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-border-subtle py-2">
            <dt className="text-caption font-normal text-text-secondary">
              {t("myRobots.topup.added")}
            </dt>
            <dd className="text-caption font-semibold text-text-primary">
              {formatMoney(done.amount, { sign: "always", suffix: "USDT" })}
            </dd>
          </div>
        </dl>

        <p className="w-full text-micro font-normal text-status-warning">
          {t("myRobots.capital.pending", { date: formatDay(done.effective_from) })}
        </p>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={t("myRobots.topup.title")}
      onClose={onClose}
      footer={
        <Button
          size="cta"
          block
          haptic="commit"
          loading={topup.isPending}
          disabled={amount === "" || compareMoney(balance, minTopup) < 0}
          onClick={submit}
        >
          {t("myRobots.topup.submit")}
        </Button>
      }
    >
      <dl className="flex w-full flex-col">
        <div className="flex items-center justify-between gap-3 py-2">
          <dt className="text-caption font-normal text-text-secondary">
            {t("myRobots.topup.current")}
          </dt>
          <dd className="text-caption font-semibold text-text-primary">
            {formatMoney(robot.invested_total, { suffix: "USDT" })}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-border-subtle py-2">
          <dt className="text-caption font-normal text-text-secondary">{t("buy.available")}</dt>
          <dd className="text-caption font-semibold text-text-primary">
            {formatMoney(balance, { suffix: "USDT" })}
          </dd>
        </div>
      </dl>

      <AmountInput
        value={amount}
        onChange={setAmount}
        error={error}
        hint={t("myRobots.topup.min", { amount: formatMoney(minTopup, { suffix: "USDT" }) })}
      />

      <AmountPresets balance={balance} min={minTopup} onPick={setAmount} />
    </Sheet>
  );
}
